import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Timesheet Calendar Page
 *
 * Displays a calendar view focused on pay periods with navigation between periods.
 * Shows daily hours worked and allows timesheet submission.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseISO, addDays, isAfter, isBefore, format } from 'date-fns';
import { Clock, AlertCircle, CheckCircle, Send, Info } from 'lucide-react';
import { useState, useMemo } from 'react';
import { TimesheetCalendar } from '../../components/time/TimesheetCalendar';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { useTimesheets, usePunchHistory } from '../../hooks/useTimeApi';
import { submitTimesheet } from '../../lib/api/time.client';
export default function Timesheets() {
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentView, setCurrentView] = useState('current');
    const { data: timesheets, isLoading: isLoadingTimesheets, error: timesheetsError } = useTimesheets();
    // Calculate date range to fetch punches for both periods
    const punchDateRange = useMemo(() => {
        if (!timesheets)
            return null;
        const currentStart = parseISO(timesheets.currentPeriod.startDate);
        const previousStart = parseISO(timesheets.previousPeriod.startDate);
        const currentEnd = parseISO(timesheets.currentPeriod.endDate);
        // Get the earliest start and latest end
        const startDate = format(isBefore(previousStart, currentStart) ? previousStart : currentStart, 'yyyy-MM-dd');
        const endDate = format(addDays(currentEnd, 1), 'yyyy-MM-dd');
        return { startDate, endDate };
    }, [timesheets]);
    // Fetch punches for the entire range
    const { data: punchData, isLoading: isLoadingPunches } = usePunchHistory(punchDateRange?.startDate, punchDateRange?.endDate);
    // Get the active period based on current view
    const activePeriod = useMemo(() => {
        if (!timesheets)
            return null;
        return currentView === 'current' ? timesheets.currentPeriod : timesheets.previousPeriod;
    }, [timesheets, currentView]);
    // Filter punches for the active period
    const periodPunches = useMemo(() => {
        if (!punchData?.punches || !activePeriod)
            return [];
        const periodStart = parseISO(activePeriod.startDate);
        const periodEnd = parseISO(activePeriod.endDate);
        return punchData.punches.filter(punch => {
            const punchDate = parseISO(punch.timestamp);
            return !isBefore(punchDate, periodStart) && !isAfter(punchDate, addDays(periodEnd, 1));
        });
    }, [punchData?.punches, activePeriod]);
    // Submit timesheet mutation
    const submitMutation = useMutation({
        mutationFn: submitTimesheet,
        onSuccess: () => {
            // Refresh timesheets
            void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
        },
    });
    const handleSubmitTimesheet = async () => {
        if (!timesheets?.currentPeriod)
            return;
        const confirmed = window.confirm(`Submit timesheet for approval?\n\n` +
            `Period: ${formatDateRange(timesheets.currentPeriod.startDate, timesheets.currentPeriod.endDate)}\n` +
            `Total Hours: ${timesheets.currentPeriod.totalHours.toFixed(2)}\n` +
            `Regular: ${timesheets.currentPeriod.regularHours.toFixed(2)} | Overtime: ${timesheets.currentPeriod.overtimeHours.toFixed(2)}\n\n` +
            `Once submitted, your manager will review your hours.`);
        if (!confirmed)
            return;
        setIsSubmitting(true);
        try {
            await submitMutation.mutateAsync({
                periodStart: timesheets.currentPeriod.startDate,
                periodEnd: timesheets.currentPeriod.endDate,
                regularHours: timesheets.currentPeriod.regularHours,
                overtimeHours: timesheets.currentPeriod.overtimeHours,
            });
            alert('Timesheet submitted successfully! Your manager will review it.');
        }
        catch (err) {
            console.error('Failed to submit timesheet:', err);
            alert(err instanceof Error ? err.message : 'Failed to submit timesheet');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handlePreviousPeriod = () => {
        if (currentView === 'current') {
            setCurrentView('previous');
        }
    };
    const handleNextPeriod = () => {
        if (currentView === 'previous') {
            setCurrentView('current');
        }
    };
    const isLoading = isLoadingTimesheets || isLoadingPunches;
    if (isLoading) {
        return (_jsxs("div", { className: "container mx-auto py-8 px-4", children: [_jsxs("div", { className: "mb-8", children: [_jsxs("h1", { className: "text-3xl font-bold flex items-center gap-2", children: [_jsx(Clock, { className: "h-8 w-8 text-blue-600" }), "My Timesheets"] }), _jsx("p", { className: "text-gray-600 mt-2", children: "View your hours for current and previous pay periods" })] }), _jsx(Card, { children: _jsxs(CardContent, { className: "p-6", children: [_jsx(Skeleton, { className: "h-8 w-64 mb-4 mx-auto" }), _jsxs("div", { className: "grid grid-cols-3 gap-4 mb-4", children: [_jsx(Skeleton, { className: "h-20" }), _jsx(Skeleton, { className: "h-20" }), _jsx(Skeleton, { className: "h-20" })] }), _jsx(Skeleton, { className: "h-[400px]" })] }) })] }));
    }
    if (timesheetsError) {
        return (_jsxs("div", { className: "container mx-auto py-8 px-4", children: [_jsx("div", { className: "mb-8", children: _jsxs("h1", { className: "text-3xl font-bold flex items-center gap-2", children: [_jsx(Clock, { className: "h-8 w-8 text-blue-600" }), "My Timesheets"] }) }), _jsxs(Alert, { variant: "destructive", children: [_jsx(AlertCircle, { className: "h-4 w-4" }), _jsxs(AlertDescription, { children: ["Failed to load timesheets: ", timesheetsError.message] })] })] }));
    }
    if (!timesheets || !activePeriod) {
        return null;
    }
    return (_jsxs("div", { className: "container mx-auto py-8 px-4 max-w-4xl", children: [_jsxs("div", { className: "mb-8", children: [_jsxs("h1", { className: "text-3xl font-bold flex items-center gap-2", children: [_jsx(Clock, { className: "h-8 w-8 text-blue-600" }), "My Timesheets"] }), _jsx("p", { className: "text-gray-600 mt-2", children: "View your hours for current and previous pay periods" })] }), _jsx(Card, { children: _jsxs(CardContent, { className: "p-6", children: [_jsx(TimesheetCalendar, { payPeriod: activePeriod, punches: periodPunches, onPreviousPeriod: handlePreviousPeriod, onNextPeriod: handleNextPeriod, hasPreviousPeriod: currentView === 'current', hasNextPeriod: currentView === 'previous', isLoading: isLoading }), currentView === 'current' && (_jsxs("div", { className: "mt-6 pt-6 border-t border-gray-200", children: [_jsxs(Button, { onClick: handleSubmitTimesheet, disabled: isSubmitting || submitMutation.isPending || timesheets.currentPeriod.totalHours === 0, className: "w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 h-12 text-base", children: [_jsx(Send, { className: "w-5 h-5 mr-2" }), isSubmitting || submitMutation.isPending ? 'Submitting...' : 'Submit for Approval'] }), timesheets.currentPeriod.totalHours === 0 && (_jsx("p", { className: "text-xs text-gray-500 text-center mt-2", children: "No hours recorded yet" }))] })), submitMutation.isError && (_jsxs(Alert, { variant: "destructive", className: "mt-4", children: [_jsx(AlertCircle, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: submitMutation.error.message })] })), submitMutation.isSuccess && (_jsxs(Alert, { className: "mt-4 bg-green-50 border-green-200", children: [_jsx(CheckCircle, { className: "h-4 w-4 text-green-600" }), _jsx(AlertDescription, { className: "text-green-800", children: "Timesheet submitted successfully!" })] }))] }) }), _jsx(Card, { className: "mt-6 bg-blue-50 border-blue-200", children: _jsx(CardContent, { className: "pt-6", children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx(Info, { className: "h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" }), _jsxs("div", { children: [_jsx("h3", { className: "font-semibold text-blue-900 mb-1", children: "About Timesheets" }), _jsxs("p", { className: "text-sm text-blue-800", children: ["Your timesheet automatically calculates hours from your punch clock activity. Regular hours (up to 8/day and 40/week) and overtime hours (", '>', "8/day or ", '>', "40/week) are calculated automatically. Hours update in real-time as you clock in and out."] })] })] }) }) })] }));
}
/**
 * Format date range display
 */
function formatDateRange(start, end) {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const formatter = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
    });
    return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
}
